import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { broadcastLogout } from "@/lib/sessionBroadcast";
import { bumpActivity, clearActivity } from "@/hooks/useInactivityTimeout";

type AppRole = "user" | "admin";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roles: AppRole[];
  /** False until the account's access level has actually been read from the server. */
  rolesResolved: boolean;
  isAdmin: boolean;
  mfaVerified: boolean;
  setMfaVerified: (v: boolean) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  roles: [],
  rolesResolved: false,
  isAdmin: false,
  mfaVerified: true,
  setMfaVerified: () => {},
  signOut: async () => {},
});

// Maximum time auth bootstrap can block the UI
const AUTH_TIMEOUT_MS = 10_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [rolesResolved, setRolesResolved] = useState(false);
  const [mfaVerified, setMfaVerifiedState] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = sessionStorage.getItem("mfa_verified");
      return stored === "true";
    }
    return true;
  });

  const setMfaVerified = (v: boolean) => {
    setMfaVerifiedState(v);
    if (typeof window !== "undefined") {
      sessionStorage.setItem("mfa_verified", String(v));
    }
  };

  // Track which user's roles we already resolved
  const currentUserIdRef = useRef<string | null>(null);
  const rolesLoadedForUserRef = useRef<string | null>(null);
  const loadingClearedRef = useRef(false);
  // Distinguish user-initiated sign-out from session expiry
  const userInitiatedSignOutRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    // Watchdog: forcibly clear loading after AUTH_TIMEOUT_MS no matter what
    const watchdog = setTimeout(() => {
      if (isMounted && !loadingClearedRef.current) {
        console.error("[Auth] Watchdog timeout — forcing loading=false");
        loadingClearedRef.current = true;
        setLoading(false);
      }
    }, AUTH_TIMEOUT_MS);

    const clearLoadingSafe = () => {
      if (isMounted && !loadingClearedRef.current) {
        loadingClearedRef.current = true;
        setLoading(false);
      }
    };

    // Blocking fetch — sets loading=true, used for fresh sign-in / account switch
    const fetchRolesBlocking = async (userId: string) => {
      try {
        const { data } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId);
        if (isMounted) {
          setRoles((data || []).map((r) => r.role));
          rolesLoadedForUserRef.current = userId;
          setRolesResolved(true);
          clearLoadingSafe();
        }
      } catch {
        // Never report "no roles" on a failed lookup — the guard would read that as
        // "not an admin" and bounce a valid admin back to the sign-in screen.
        if (isMounted) {
          clearLoadingSafe();
          setTimeout(() => { if (isMounted) void fetchRolesSilent(userId); }, 1200);
        }
      }
    };

    // Silent fetch — no loading flash, keeps previous roles on failure
    const fetchRolesSilent = async (userId: string) => {
      try {
        const { data } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId);
        if (isMounted && data) {
          setRoles(data.map((r) => r.role));
          rolesLoadedForUserRef.current = userId;
          setRolesResolved(true);
        }
      } catch {
        // keep previous roles — don't clear; retry shortly so the guard can decide
        if (isMounted) setTimeout(() => { if (isMounted) void fetchRolesSilent(userId); }, 1500);
      }
    };

    // 1. Restore session FIRST, then set up listener
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          currentUserIdRef.current = session.user.id;
          await fetchRolesBlocking(session.user.id);
        }
      } catch (err) {
        console.error("[Auth] Session restore failed:", err);
      } finally {
        if (isMounted) {
          clearLoadingSafe();
        }
      }

      // 2. Set up listener AFTER getSession to avoid race conditions
      const { data: { subscription: sub } } = supabase.auth.onAuthStateChange(
        (event, session) => {
          if (!isMounted) return;
          setSession(session);
          setUser(session?.user ?? null);

          if (event === "SIGNED_OUT") {
            currentUserIdRef.current = null;
            rolesLoadedForUserRef.current = null;
            setRoles([]);
            setRolesResolved(false);
            // Session expiry (not user-initiated): briefly show loading to prevent flash
            if (!userInitiatedSignOutRef.current) {
              loadingClearedRef.current = false;
              setLoading(true);
              setTimeout(() => { clearLoadingSafe(); }, 50);
            }
            userInitiatedSignOutRef.current = false;
            clearLoadingSafe();
          } else if (event === "SIGNED_IN" || event === "USER_UPDATED") {
            const newUserId = session?.user?.id ?? null;
            const isNewUser = newUserId !== currentUserIdRef.current;
            const rolesAlreadyLoaded = newUserId === rolesLoadedForUserRef.current;
            currentUserIdRef.current = newUserId;

            // Seed inactivity timestamp on fresh sign-in so timer starts now.
            if (newUserId && isNewUser) bumpActivity();

            if (newUserId && isNewUser) setRolesResolved(rolesAlreadyLoaded);

            if (newUserId && isNewUser && !rolesAlreadyLoaded) {
              // Fresh sign-in or account switch → blocking
              loadingClearedRef.current = false;
              setLoading(true);
              fetchRolesBlocking(newUserId);
            } else if (newUserId && !rolesAlreadyLoaded) {
              // Same user but roles not loaded yet (edge case)
              fetchRolesSilent(newUserId);
            }
            // Same user + roles already loaded → do nothing (tab focus case)
          } else if (event === "INITIAL_SESSION") {
            // Safety net: load roles if session exists but roles not yet loaded
            const uid = session?.user?.id ?? null;
            currentUserIdRef.current = uid;
            if (uid && uid !== rolesLoadedForUserRef.current) {
              fetchRolesSilent(uid);
            }
          }
          // TOKEN_REFRESHED — session updated silently above
        }
      );
      subscription = sub;
    };

    initializeAuth();

    return () => {
      isMounted = false;
      clearTimeout(watchdog);
      subscription?.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    userInitiatedSignOutRef.current = true;
    // Set loading to bypass ProtectedRoute's isTransitioning guard
    loadingClearedRef.current = false;
    setLoading(true);
    // Notify other tabs and clear shared activity stamp
    try { broadcastLogout("manual"); } catch { /* ignore */ }
    clearActivity();
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRoles([]);
    setRolesResolved(false);
    setMfaVerifiedState(true);
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("mfa_verified");
    }
    currentUserIdRef.current = null;
    rolesLoadedForUserRef.current = null;
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, roles, rolesResolved, isAdmin: roles.includes("admin"), mfaVerified, setMfaVerified, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
